// Report a Problem from the guest page, Stage 5 piece 4b. Words: WORK_REQUEST_WORDS.md (guest section,
// approved by Robert 2026-10-11). Mockup: MOCKUP_GUEST_REPORT.html (approved 2026-10-11).
//
// Anyone who scans the label. No sign-in, name optional, asset from the scan.
// Plain-word picks. Smell, Heat or Sparks start High, the rest Medium. The ESP Manager screens it.
// It lands in Work Requests as a Guest report and emails the site's Alerts contacts.
// One report per device every 5 minutes, checked on the server.
//
// Pure: data in, data out. Unit tested.

import { siteContactsFor, siteNameOf, type AlertWorld, type PlannedAlert } from "./alerts";
import type { EmailRequest } from "./email";
import { startedTextOf } from "./work-started";
import { priorityOf, type RequestLine, type WorkRequestRow } from "./work-requests";

export const GUEST_PICKS = ["Smell", "Noise", "Heat", "Sparks", "Damage", "Door Open Or Cover Missing", "Water", "Other"] as const;
export type GuestPick = (typeof GUEST_PICKS)[number];

const HIGH_PICKS: readonly GuestPick[] = ["Smell", "Heat", "Sparks"];

/** Minutes between reports from one device. */
export const GUEST_REPORT_GAP_MINUTES = 5;
export const NOTE_MAX = 300;
export const NAME_MAX = 80;
export const NO_NAME = "No name given";
export const GUEST_REASON = "Guest report";
export const TOO_SOON = "You just sent a report. Try again in a few minutes.";

/** "Door Open Or Cover Missing" reads as "Door open or cover missing" in lists and email. */
export function pickText(p: GuestPick): string {
  return p.charAt(0) + p.slice(1).toLowerCase();
}

export function guestLinesOf(picks: readonly string[]): RequestLine[] {
  return GUEST_PICKS.filter((p) => picks.includes(p)).map((p) => ({ finding: pickText(p), priority: HIGH_PICKS.includes(p) ? "High" : "Medium" }));
}

export interface GuestReportInput {
  picks: string[];
  note: string;
  name: string;
}

export function guestReportProblem(i: GuestReportInput): string | null {
  if (!guestLinesOf(i.picks).length) return "Pick what you noticed.";
  if (i.picks.includes("Other") && !i.note.trim()) return "Write one line about what you noticed.";
  return null;
}

/** True when the last report from this device is inside the gap. */
export function tooSoon(lastIso: string | null, nowIso: string): boolean {
  if (!lastIso) return false;
  return Date.parse(nowIso) - Date.parse(lastIso) < GUEST_REPORT_GAP_MINUTES * 60_000;
}

export interface NewGuestRequestInput {
  requestId: string;
  siteNumber: string;
  assetId: string;
  location: string;
  equipment: string;
  input: GuestReportInput;
  serviceabilityAtReport: string;
  /** Stamped for the abuse guard. Never shown on a screen. */
  deviceKey: string;
  today: string;
  nowIso: string;
}

export function newGuestRequestOf(i: NewGuestRequestInput): WorkRequestRow & { deviceKey: string } {
  const lines = guestLinesOf(i.input.picks);
  const name = i.input.name.trim().slice(0, NAME_MAX);
  return {
    requestId: i.requestId,
    source: "guest",
    note: i.input.note.trim().slice(0, NOTE_MAX),
    siteNumber: i.siteNumber,
    assetId: i.assetId,
    location: i.location,
    equipment: i.equipment,
    jspNumber: "",
    qewId: "",
    holderName: name || NO_NAME,
    priority: priorityOf(lines),
    lines,
    status: "Open",
    workOrderNumber: "",
    workOrderSystem: "",
    closedHow: null,
    resolution: "",
    closedByName: "",
    closedOn: null,
    serviceabilityAtReport: i.serviceabilityAtReport,
    leftWorseNote: "",
    createdOn: i.today,
    createdAt: i.nowIso,
    deviceKey: i.deviceKey,
  };
}

/** One short email to the site's Alerts contacts. Null when the site has none. */
export function guestReportAlert(r: WorkRequestRow, world: AlertWorld): PlannedAlert | null {
  const to = siteContactsFor(world, r.siteNumber, "alerts");
  if (!to.length) return null;
  const site = siteNameOf(world, r.siteNumber);
  const sign = world.companyName ? `Sent by SafeTag for the ${world.companyName} Electrical Safety Program.` : "Sent by SafeTag for the Electrical Safety Program.";
  const email: EmailRequest = {
    siteNumber: r.siteNumber,
    siteName: site,
    companyName: world.companyName,
    to,
    toLabel: `${site} alerts`,
    subject: `Problem Reported · ${r.equipment || "Equipment"} · ${r.assetId} · ${r.requestId}`,
    headline: "Someone reported a problem at the equipment.",
    lines: [`Reported from the SafeTag label, no sign-in. ${r.priority === "High" ? "Check it today." : "Check it within a week."}`],
    rows: [
      { label: "Noticed", value: r.lines.map((l) => l.finding).join(", ") },
      ...(r.note ? [{ label: "Note", value: r.note }] : []),
      { label: "Priority", value: r.priority },
      { label: "Reported by", value: r.holderName || NO_NAME },
      { label: "Equipment", value: r.equipment || "Not on record" },
      { label: "Location", value: r.location || "Not on record" },
      { label: "Asset ID", value: r.assetId },
      { label: "Reported", value: startedTextOf(r.createdAt) },
      { label: "Request", value: r.requestId },
    ],
    button: null,
    footer: `You get this because you are an Alerts contact at ${site}. ${sign}`,
    reason: GUEST_REASON,
    recordId: r.requestId,
  };
  return { key: `guest-report-${r.requestId}`, emails: [email] };
}

// ---- The sheet on the guest page ----

export interface GuestSheet {
  picks: string[];
  note: string;
  name: string;
  busy: boolean;
  error: string;
  /** The request number once sent. */
  doneId: string;
}

export const EMPTY_SHEET: GuestSheet = { picks: [], note: "", name: "", busy: false, error: "", doneId: "" };

const esc = (v: unknown) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export function guestReportSheetHtml(s: GuestSheet, assetLine: string): string {
  const head = `<div class="g-overlay show" data-g="close"></div>
    <div class="g-bottom-sheet open" role="dialog" aria-label="Report a Problem">
      <div class="g-sheet-handle"></div>`;
  if (s.doneId)
    return `${head}<div class="g-report-success show">
        <div class="g-success-icon gr-done">✓</div><div class="g-success-title gr-done-t">Reported</div>
        <div class="g-success-sub">Maintenance will look at it. Reference ${esc(s.doneId)}.</div>
        <button class="g-success-close" type="button" data-g="close">Done</button></div></div>`;
  const chips = GUEST_PICKS.map(
    (p) => `<button class="g-priority-pill gr-pick${s.picks.includes(p) ? " med active" : ""}" type="button" data-gpick="${esc(p)}" aria-pressed="${s.picks.includes(p)}">${esc(p)}</button>`,
  ).join("");
  const can = guestLinesOf(s.picks).length > 0 && !s.busy;
  return `${head}
      <div class="g-sheet-header"><span class="g-sheet-title">Report a Problem</span><button class="g-sheet-close" type="button" data-g="close">✕</button></div>
      <div class="g-sheet-asset-ref">${esc(assetLine)}</div>
      <div class="g-form-section"><div class="gr-warn">Smoke, fire or arcing? Leave the area and call it in.</div></div>
      <div class="g-form-section"><div class="g-form-label">What did you notice? <span>Pick one or more</span></div><div class="g-priority-row gr-picks">${chips}</div></div>
      <div class="g-form-section"><div class="g-form-label">Anything else? <span>${s.picks.includes("Other") ? "Required for Other" : "Optional"}</span></div>
        <textarea class="g-obs-textarea" id="gr-note" rows="2" maxlength="${NOTE_MAX}" placeholder="One line about what you saw">${esc(s.note)}</textarea></div>
      <div class="g-form-section"><div class="g-form-label">Your name <span>Optional</span></div>
        <input class="g-name-input" id="gr-name" maxlength="${NAME_MAX}" autocomplete="name" placeholder="Leave blank to report without a name" value="${esc(s.name)}"></div>
      ${s.error ? `<div class="g-form-section"><div class="gr-err" role="status">${esc(s.error)}</div></div>` : ""}
      <div class="g-form-section"><button class="g-sheet-submit" type="button" data-g="send-report"${can ? "" : " disabled"}>${s.busy ? "Sending…" : "Send Report"}</button></div>
    </div>`;
}
